import { useNavigate, useSearch } from '@tanstack/react-router'
import {
    ChartBarIcon,
    AcademicCapIcon,
    TrophyIcon,
    PlayIcon,
    CommandLineIcon,
} from '@heroicons/react/24/outline'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button } from '@duck/ui'
import { useAuth } from '@/hooks/useAuth'
import { useEffect, useRef } from 'react'
import { WaitingList } from '../WaitingList/WaitingList'
import { Logo } from '../Misc/Logo'

export const HomePage = () => {
    const { openSignupDialog, isAuthenticated, isInitializing } = useAuth()
    const navigate = useNavigate()
    const search = useSearch({ from: '/' })
    const waitingListRef = useRef<HTMLDivElement>(null)

    // Check for token parameter and open signup dialog (only after auth is ready so we know if user is logged in)
    useEffect(() => {
        if (isInitializing || !(search.token || search.atoken)) {
            return
        }
        const timeout = setTimeout(() => {
            openSignupDialog(search.token || search.atoken)
        }, 256)
        return () => clearTimeout(timeout)
    }, [search.token, search.atoken, openSignupDialog, isInitializing])

    const features = [
        {
            icon: ChartBarIcon,
            title: 'Real Analytical Situations',
            description: 'Ambiguity! Trade-offs! Stakeholders! Welcome to the real life.'
        },
        {
            icon: AcademicCapIcon,
            title: 'Learn By Doing',
            description: 'Hands-On Analysis in an advanced online workspace'
        },
        {
            icon: TrophyIcon,
            title: 'Personalized Feedback',
            description: 'Specific, Prioritized notes on your work. We show the bar and how to reach it.'

        }
    ]

    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 max-w-6xl space-y-8 mt-8">
                {/* Hero Section */}
                <div className="text-center space-y-6">
                    <div className="space-y-4">
                        <h1 className="text-4xl md:text-6xl font-bold text-foreground">
                            Don't practice SQL. Practice Analysis.
                        </h1>
                        <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                            The best (and only) place to practice Data Analysis
                        </p>
                    </div>
                    <div className="h-1 w-full border-t border-border" />
                    {!isAuthenticated ? (
                        <div ref={waitingListRef}>
                            <WaitingList />
                        </div>
                    ) : (
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2">
                            <Button
                                onClick={() => {
                                    navigate({ to: '/challenges/available' })
                                }}
                                size="lg"
                                className="flex items-center space-x-2 px-6 py-6 bg-accent hover:bg-accent/80 text-white text-lg font-medium rounded-lg transition-colors duration-200"
                            >
                                <PlayIcon className="w-6 h-6" />
                                <span>Start Analyzing</span>
                            </Button>

                        </div>
                    )}
                </div>
                {/* Features Section */}
                <div className="space-y-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold text-foreground mb-4">Why <Logo />?</h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            3 pillars of success based on thousands of hours of experience
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, index) => {
                            const IconComponent = feature.icon
                            return (
                                <div
                                    key={index}
                                    className="bg-card rounded-lg shadow-sm border border-border p-6 text-center hover:shadow-md transition-shadow duration-200"
                                >
                                    <div className="flex justify-center mb-4">
                                        <div className="p-3 bg-accent/10 rounded-full">
                                            <IconComponent className="w-8 h-8 text-accent" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-semibold text-foreground mb-3">
                                        {feature.title}
                                    </h3>
                                    <p className="text-muted-foreground leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Call to Action Section */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
                    <div className="space-y-4">
                        <div className="flex justify-center">
                            <div className="p-4 bg-accent/10 rounded-full">
                                <CommandLineIcon className="w-12 h-12 text-accent" />
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground">The XP-Lab Manifesto</h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Why we exist, and how we think about building real analysts.
                        </p>
                        <Accordion type="single" collapsible className="w-full max-w-xl mx-auto text-left">
                            <AccordionItem value="manifesto" className="border-none">
                                <AccordionTrigger className="text-lg font-semibold text-foreground border-0 justify-center text-center">
                                    Read the manifesto
                                </AccordionTrigger>
                                <AccordionContent>
                                    <div className="space-y-4 text-muted-foreground leading-relaxed text-left">
                                        <p>Memorizing SQL syntax never defined the analyst&apos;s work.</p>
                                        <p>Especially now, with LLMs doing autocompletion on steroids.</p>
                                        <p>So no - we&apos;re not here to teach you how to pause a YouTube tutorial every two seconds because the instructor types too fast.</p>
                                        <p>We&apos;re here to turn you into someone who can <em>actually do the work</em>.</p>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">Analysis is a craft.</h3>
                                            <p>It&apos;s messy, iterative, full of dead ends, bad joins, wrong assumptions, and moments where your brain whispers:</p>
                                            <blockquote className="border-l-2 border-border pl-3 italic text-foreground">"Wait... this number makes no sense."</blockquote>
                                            <p>That&apos;s the real job.</p>
                                            <p>And that&apos;s what we train.</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">We believe in hands-on, not hand-holding.</h3>
                                            <p>You learn by doing real tasks, making real mistakes, and getting real feedback.</p>
                                            <p>Not stickers.</p>
                                            <p>Not "great job".</p>
                                            <p>Actual feedback - the kind that stings a bit, teaches a lot, and makes you better tomorrow.</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">We reject the fantasy of the &apos;perfect solution&apos;.</h3>
                                            <p>Data problems don&apos;t come with answer keys.</p>
                                            <p>They come with ambiguity, annoying stakeholders, missing values, and deadlines.</p>
                                            <p>So our tasks do too.</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">We treat learning like training, not performance.</h3>
                                            <p>You&apos;re here to build your analytical muscle.</p>
                                            <p>Muscle comes from repetition, consistency, and pushing past the point where you&apos;d normally stop.</p>
                                            <p>No performative CV fluff drenched in LLM-generated stink.</p>
                                            <p>No "AI-ready unicorn pipeline ninja" nonsense.</p>
                                            <p>You are not embarking on a hero&apos;s journey - you&apos;re not Odysseus.</p>
                                            <p>And honestly, you wouldn&apos;t want to be.</p>
                                            <p>Remember how the Odyssey ends?</p>
                                            <p>Exactly.</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">We believe you grow fastest when your work gets challenged.</h3>
                                            <p>Our agents - human and AI - don&apos;t just check whether your code runs.</p>
                                            <p>They critique your reasoning, your choices, your story, your communication.</p>
                                            <p>Everything a real senior analyst would do, minus the office politics.</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">We believe failure is the tuition of mastery.</h3>
                                            <p>Nobody improves by being coddled.</p>
                                            <p>You improve by trying, failing, adjusting, and trying again.</p>
                                            <p>We just make the cycle shorter.</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">XP-Lab exists so that you</h3>
                                            <p>can walk into any real-world mess, make sense of it, and deliver clarity.</p>
                                            <p>That&apos;s it.</p>
                                            <p>No gurus.</p>
                                            <p>No shortcuts.</p>
                                            <p>Just the craft.</p>
                                            <p>And you - getting better at it, one task at a time.</p>
                                        </div>

                                        <div className="flex justify-center pt-4">
                                            <button
                                                onClick={() => {
                                                    if (isAuthenticated) {
                                                        navigate({ to: '/challenges/available' })
                                                    } else {
                                                        waitingListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                                    }
                                                }}
                                                className="inline-flex items-center space-x-2 px-6 py-3 bg-accent hover:bg-accent/80 text-white text-lg font-medium rounded-lg transition-colors duration-200"
                                            >
                                                <PlayIcon className="w-6 h-6" />
                                                <span>Get Started Now</span>
                                            </button>
                                        </div>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>
                </div>

                {/* Footer Note */}
                <div className="text-center pb-8">
                    <p className="text-muted text-sm">
                        Train like you work • New challenges added regularly • Progress tracked automatically
                    </p>
                </div>
            </div>
        </div>
    )
}
